import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlaskConical } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectSampleDataError,
  selectSampleDataFetchStatus,
  selectSampleDataMutationStatus,
  selectSampleDataStatus,
} from "../sample-data-slice";
import {
  fetchSampleDataStatus,
  loadSampleData,
  removeSampleData,
} from "../slice-extensions/sample-data-slice-extension";
import { LoadingStatus } from "../../sessions/models/LoadingStatus";

/**
 * Organization-settings card (Owner/Admin): load the localized sample dataset,
 * see what it contains, and remove exactly that data again.
 */
const SampleDataCard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectSampleDataStatus);
  const fetchStatus = useAppSelector(selectSampleDataFetchStatus);
  const mutationStatus = useAppSelector(selectSampleDataMutationStatus);
  const lastError = useAppSelector(selectSampleDataError);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);

  useEffect(() => {
    if (status === null && fetchStatus === LoadingStatus.Idle) {
      dispatch(fetchSampleDataStatus());
    }
  }, [status, fetchStatus, dispatch]);

  const busy = mutationStatus === LoadingStatus.Loading;
  const active = status?.active ?? false;
  const counts = status?.counts;

  const countsLine = counts
    ? [
        [t("sampleData.countMembers", "Members"), counts.members],
        [t("sampleData.countGroups", "Groups"), counts.groups],
        [t("sampleData.countSessions", "Sessions"), counts.sessions],
        [t("sampleData.countContracts", "Contracts"), counts.contracts],
        [t("sampleData.countPayments", "Payments"), counts.payments],
        [t("sampleData.countTrialRequests", "Trial requests"), counts.trialRequests],
      ]
        .filter(([, value]) => (value as number) > 0)
        .map(([label, value]) => `${label}: ${value}`)
        .join(" · ")
    : "";

  const handleLoad = async () => {
    const result = await dispatch(loadSampleData({ languageCode: i18n.language }));
    if (loadSampleData.fulfilled.match(result)) {
      // The whole workspace changed — remount everything so every list and card is fresh.
      window.location.reload();
    }
  };

  const handleRemove = async () => {
    const result = await dispatch(removeSampleData());
    if (removeSampleData.fulfilled.match(result)) {
      setConfirmingRemoval(false);
      window.location.reload();
    }
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
          <FlaskConical size={20} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold leading-tight">
            {t("sampleData.cardTitle", "Sample data")}
          </h2>
          <p className="text-sm text-grey-50 dark:text-grey-70 mt-0.5">
            {active
              ? t("sampleData.cardSubtitleActive", "Sample data is loaded. {{counts}}", {
                  counts: countsLine,
                })
              : t(
                  "sampleData.cardSubtitle",
                  "Explore with example members, sessions with attendance, contracts and payments. All of it is removable in one click.",
                )}
          </p>
        </div>
      </div>

      {!active && (
        <button type="button" className="btn btn-primary" onClick={handleLoad} disabled={busy}>
          {busy
            ? t("sampleData.loading", "Setting up sample data…")
            : t("sampleData.loadAction", "Load sample data")}
        </button>
      )}

      {active && !confirmingRemoval && (
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setConfirmingRemoval(true)}
          disabled={busy}
        >
          {t("sampleData.removeAction", "Remove sample data")}
        </button>
      )}

      {active && confirmingRemoval && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-grey-10 dark:text-grey-90">
            {t(
              "sampleData.removeConfirmText",
              "This removes every sample entry — including anything you added to sample members or sessions. Your own data stays untouched.",
            )}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className="btn btn-primary" onClick={handleRemove} disabled={busy}>
              {busy
                ? t("sampleData.removing", "Removing…")
                : t("sampleData.removeConfirmAction", "Yes, remove it")}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setConfirmingRemoval(false)}
              disabled={busy}
            >
              {t("common.cancel", "Cancel")}
            </button>
          </div>
        </div>
      )}

      {status != null && status.issues.length > 0 && (
        <p className="mt-3 text-sm text-grey-50 dark:text-grey-70">
          {t("sampleData.issuesIntro", "Some items could not be created:")}{" "}
          {status.issues.join("; ")}
        </p>
      )}

      {lastError && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400">{lastError.message}</p>
      )}
    </div>
  );
};

export default SampleDataCard;
